import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { SERVICES } from "@/data/site";

const PROCESS = [
  { n: "01", title: "Discovery", body: "Workshops, audits and market intel. We learn your business before we touch a pixel." },
  { n: "02", title: "Blueprint", body: "Architecture, wireframes and technical strategy — the plan everyone signs off on." },
  { n: "03", title: "Kinetic Build", body: "Design and engineering in tight loops. You see progress weekly, not monthly." },
  { n: "04", title: "Launch & Grow", body: "QA, deployment, measurement. Then we iterate toward the numbers that matter." },
];

const Services = () => (
  <main data-testid="services-page" className="pt-20">
    <section className="relative py-24 lg:py-36 overflow-hidden grid-bg">
      <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] bg-cyan-400/10 blur-[140px] rounded-full" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <MaskedLine delay={0.1}>
          <Overline>Our Services</Overline>
        </MaskedLine>
        <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl mt-6">
          <MaskedLine delay={0.25}>Three ways we</MaskedLine>
          <MaskedLine delay={0.4}>
            move markets<span className="text-cyan-400">.</span>
          </MaskedLine>
        </h1>
      </div>
    </section>

    <section className="pb-24 lg:pb-32 bg-white text-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 space-y-6">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.05}>
            <article
              data-testid={s.testId}
              className="group border border-zinc-200 rounded-2xl bg-white hover:bg-zinc-50 transition-colors duration-500 p-8 lg:p-14 grid lg:grid-cols-12 gap-10"
            >
              <div className="lg:col-span-1">
                <span className="font-mono-accent text-cyan-600 text-lg">{s.index}/</span>
              </div>
              <div className="lg:col-span-6">
                <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl group-hover:text-cyan-600 transition-colors">
                  {s.title}
                </h2>
                <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-zinc-500 mt-3">
                  {s.hook}
                </p>
                <p className="text-zinc-600 text-sm sm:text-base leading-relaxed mt-6">
                  {s.description}
                </p>
                {s.spotlight && (
                  <div className="mt-8 border border-cyan-500/40 bg-cyan-400/10 p-6">
                    <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-cyan-600">
                      Our own marketing platform
                    </p>
                    <p className="text-zinc-700 text-sm leading-relaxed mt-3">
                      <strong className="text-black">AI Warehouse</strong> is our own marketing
                      platform dedicated to promoting AI products and services — connecting
                      innovative AI builders with the business decision makers searching for
                      them. If you have an AI product or service, this is where it gets found.
                    </p>
                    <a
                      href="https://ai-warehouse.net/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="cta-link-ai-warehouse"
                      className="inline-flex items-center gap-2 bg-cyan-400 text-black font-display font-bold text-xs uppercase tracking-wider px-6 py-3 mt-5 hover:bg-white transition-colors"
                    >
                      Explore AI Warehouse <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
              <div className="lg:col-span-5">
                <ul className="space-y-4 lg:border-l lg:border-zinc-200 lg:pl-10">
                  {s.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-sm text-zinc-700">
                      <Check className="w-4 h-4 text-cyan-600 shrink-0 mt-0.5" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="py-24 lg:py-32 bg-[#050507] border-t border-zinc-800/80" data-testid="services-process">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Overline>The Process</Overline>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl lg:text-4xl mt-4">
            From brief to breakthrough
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 mt-14 border-t border-l border-zinc-800">
          {PROCESS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.08}>
              <div className="h-full border-b border-r border-zinc-800 p-8 hover:bg-black transition-colors duration-500 group">
                <span className="font-display font-extrabold text-4xl text-stroke group-hover:text-cyan-400 group-hover:[-webkit-text-stroke:0px] transition-all">
                  {step.n}
                </span>
                <h3 className="font-display font-bold uppercase text-lg mt-6">{step.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-3">{step.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </main>
);

export default Services;
